import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, Plus, Radar, Trash2 } from "lucide-react";
import { useCan } from "@/hooks/use-operator";
import { RequireCapability } from "@/components/require-capability";
import type { DeviceId, EquipmentId } from "@brewbase/db/types";

export const Route = createFileRoute("/settings/devices")({
  component: () => (
    <RequireCapability cap="brewcontrol-nodes:manage">
      <DeviceAdminPage />
    </RequireCapability>
  ),
});

interface GatewayDevice {
  id: string;
  device_type: string;
  name: string;
  address: string;
  online: boolean;
}

const gatewayDeviceTypes = [
  { value: "kamoer_pump", label: "Pump" },
  { value: "rtd_node", label: "Temperature node" },
];

function DeviceAdminPage() {
  const healthQuery = trpc.devices.health.useQuery(undefined, {
    refetchInterval: 10_000,
  });
  const gatewayStatus = healthQuery.data?.status ?? "loading";

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-3xl space-y-8 px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-sm font-medium">Devices</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Register the nodes and devices the site gateway connects to.
            </p>
          </div>
          <GatewayStatus status={gatewayStatus} />
        </div>

        <BrewLinkNodesSection gatewayOk={gatewayStatus === "ok"} />
        <EquipmentBindingsSection />
        <GatewayDevicesSection gatewayOk={gatewayStatus === "ok"} />
      </div>
    </div>
  );
}

function GatewayStatus({ status }: { status: string }) {
  const label =
    status === "ok"
      ? "Gateway online"
      : status === "unreachable"
        ? "Gateway offline"
        : status === "not_configured"
          ? "Gateway not configured"
          : "Connecting…";
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span
        className={cn(
          "size-2 rounded-full",
          status === "ok" && "bg-green-9",
          status === "unreachable" && "bg-red-9",
          status === "not_configured" && "bg-olive-8",
          status === "loading" && "bg-olive-8 animate-pulse",
        )}
      />
      {label}
    </div>
  );
}

function BrewLinkNodesSection({ gatewayOk }: { gatewayOk: boolean }) {
  const utils = trpc.useUtils();
  const nodesQuery = trpc.deviceRegistry.listNodes.useQuery(undefined, {
    refetchInterval: 5_000,
  });
  const [host, setHost] = useState("");

  const addMutation = trpc.deviceRegistry.addNode.useMutation({
    onSuccess: () => {
      toast.success("BrewLink node added");
      utils.deviceRegistry.listNodes.invalidate();
      setHost("");
    },
    onError: (e) => toast.error(e.message),
  });
  const removeMutation = trpc.deviceRegistry.removeNode.useMutation({
    onSuccess: () => {
      toast.success("BrewLink node removed");
      utils.deviceRegistry.listNodes.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const nodes = nodesQuery.data ?? [];

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-medium">BrewLink nodes</h2>
        <p className="text-xs text-muted-foreground">
          ESP32 nodes connected over WebSocket. Identified by MAC on first
          connect.
        </p>
      </div>

      <div className="border border-olive-6 divide-y divide-olive-6">
        {nodes.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-olive-11">
            No BrewLink nodes configured.
          </div>
        ) : (
          nodes.map((node) => (
            <div
              key={node.host}
              className="flex items-center gap-3 px-3 py-2"
            >
              <span
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  node.connected ? "bg-green-9" : "bg-red-9",
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">
                  {node.name ?? node.host}
                </div>
                <div className="flex gap-3 font-mono text-xs text-olive-11">
                  <span>{node.host}</span>
                  {node.mac && <span>{node.mac}</span>}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                disabled={!node.mac || removeMutation.isPending}
                onClick={() => node.mac && removeMutation.mutate({ mac: node.mac })}
                aria-label="Remove node"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (host.trim()) addMutation.mutate({ host: host.trim() });
        }}
      >
        <Input
          value={host}
          onChange={(e) => setHost(e.target.value)}
          placeholder="host (e.g. uht.local or 10.0.0.12:80)"
          className="h-8 font-mono text-sm"
          disabled={!gatewayOk}
        />
        <Button
          type="submit"
          size="sm"
          disabled={!gatewayOk || addMutation.isPending || !host.trim()}
        >
          {addMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
          Add node
        </Button>
      </form>
    </section>
  );
}

function EquipmentBindingsSection() {
  const canManage = useCan("brewcontrol-nodes:manage");
  const utils = trpc.useUtils();
  const devicesQuery = trpc.deviceRegistry.list.useQuery(undefined, {
    refetchInterval: 5_000,
  });
  // Every unit, so a binding to a retired one still shows its name; only
  // in-service units are offered for new bindings.
  const equipmentQuery = trpc.equipment.list.useQuery({
    includeOutOfService: true,
  });
  const { data: bindable = [] } = trpc.equipment.list.useQuery();

  const bindMutation = trpc.deviceRegistry.bindEquipment.useMutation({
    onSuccess: () => {
      toast.success("Equipment bound");
      utils.deviceRegistry.list.invalidate();
      utils.deviceRegistry.get.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const unbindMutation = trpc.deviceRegistry.unbindEquipment.useMutation({
    onSuccess: () => {
      toast.success("Equipment unbound");
      utils.deviceRegistry.list.invalidate();
      utils.deviceRegistry.get.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  // UX gate only — the bind/unbind mutations are capability-enforced server-side.
  if (!canManage) return null;

  const devices = devicesQuery.data ?? [];
  const equipment = equipmentQuery.data ?? [];
  const busy = bindMutation.isPending || unbindMutation.isPending;

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-medium">Equipment bindings</h2>
        <p className="text-xs text-muted-foreground">
          Link a registered device to the equipment it controls.
        </p>
      </div>

      <div className="border border-olive-6 divide-y divide-olive-6">
        {devices.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-olive-11">
            No registered devices.
          </div>
        ) : (
          devices.map((device) => {
            const boundName = device.binding
              ? equipment.find((e) => e.id === device.binding!.equipment_id)
                  ?.name
              : null;
            return (
              <div
                key={device.id}
                className="flex items-center gap-3 px-3 py-2"
              >
                <span
                  className={cn(
                    "size-2 shrink-0 rounded-full",
                    device.status === "ONLINE" ? "bg-green-9" : "bg-red-9",
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">
                    {device.name ?? device.hardware_id}
                  </div>
                  <div className="font-mono text-xs text-olive-11">
                    {device.hardware_id}
                  </div>
                </div>
                <DeviceBindingControl
                  bound={
                    device.binding
                      ? { name: boundName ?? "Unknown equipment" }
                      : null
                  }
                  options={bindable.map((e) => ({ id: e.id, name: e.name }))}
                  busy={busy}
                  onBind={(equipmentId) =>
                    bindMutation.mutate({
                      deviceId: device.id as DeviceId,
                      equipmentId: equipmentId as EquipmentId,
                    })
                  }
                  onUnbind={() =>
                    unbindMutation.mutate({ deviceId: device.id as DeviceId })
                  }
                />
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function DeviceBindingControl({
  bound,
  options,
  busy,
  onBind,
  onUnbind,
}: {
  bound: { name: string } | null;
  options: { id: number; name: string }[];
  busy: boolean;
  onBind: (equipmentId: number) => void;
  onUnbind: () => void;
}) {
  const [sel, setSel] = useState<string>("");
  if (bound) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-olive-12">{bound.name}</span>
        <Button variant="ghost" size="sm" onClick={onUnbind} disabled={busy}>
          Unbind
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <select
        value={sel}
        onChange={(e) => setSel(e.target.value)}
        className="h-7 border border-olive-6 bg-olive-2 px-1.5 text-sm text-olive-12"
      >
        <option value="">Bind equipment…</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      <Button
        variant="ghost"
        size="sm"
        disabled={!sel || busy}
        onClick={() => sel && onBind(Number(sel))}
      >
        Bind
      </Button>
    </div>
  );
}

function GatewayDevicesSection({ gatewayOk }: { gatewayOk: boolean }) {
  const utils = trpc.useUtils();
  const devicesQuery = trpc.devices.list.useQuery(undefined, {
    refetchInterval: 5_000,
  });
  const [deviceType, setDeviceType] = useState("kamoer_pump");
  const [address, setAddress] = useState("");
  const [name, setName] = useState("");

  const addMutation = trpc.devices.add.useMutation({
    onSuccess: () => {
      toast.success("Device added");
      utils.devices.list.invalidate();
      setAddress("");
      setName("");
    },
    onError: (e) => toast.error(e.message),
  });
  const removeMutation = trpc.devices.remove.useMutation({
    onSuccess: () => {
      toast.success("Device removed");
      utils.devices.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const discoverMutation = trpc.devices.discover.useMutation({
    onSuccess: (data) => {
      const count = (data as { discovered?: unknown[] }).discovered?.length ?? 0;
      toast.success(`Discovery complete: ${count} device(s) found`);
      utils.devices.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const devices: GatewayDevice[] =
    (devicesQuery.data as { devices?: GatewayDevice[] })?.devices ?? [];

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium">Gateway devices</h2>
          <p className="text-xs text-muted-foreground">
            Third-party devices managed by the gateway (pumps, sensors).
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={!gatewayOk || discoverMutation.isPending}
          onClick={() => discoverMutation.mutate({})}
        >
          {discoverMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Radar className="size-4" />
          )}
          Discover
        </Button>
      </div>

      <div className="border border-olive-6 divide-y divide-olive-6">
        {devices.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-olive-11">
            No gateway devices.
          </div>
        ) : (
          devices.map((device) => (
            <div key={device.id} className="flex items-center gap-3 px-3 py-2">
              <span
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  device.online ? "bg-green-9" : "bg-red-9",
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{device.name}</div>
                <div className="flex gap-3 font-mono text-xs text-olive-11">
                  <span>{device.device_type}</span>
                  <span>{device.address}</span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate({ id: device.id })}
                aria-label="Remove device"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (address.trim())
            addMutation.mutate({
              type: deviceType,
              address: address.trim(),
              name: name.trim() || undefined,
            });
        }}
      >
        <select
          value={deviceType}
          onChange={(e) => setDeviceType(e.target.value)}
          className="h-8 border border-olive-6 bg-olive-2 px-1.5 text-sm text-olive-12"
          disabled={!gatewayOk}
        >
          {gatewayDeviceTypes.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <Input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={deviceType === "rtd_node" ? "hostname" : "IP address"}
          className="h-8 font-mono text-sm"
          disabled={!gatewayOk}
        />
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="name (optional)"
          className="h-8 text-sm"
          disabled={!gatewayOk}
        />
        <Button
          type="submit"
          size="sm"
          disabled={!gatewayOk || addMutation.isPending || !address.trim()}
        >
          {addMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
          Add
        </Button>
      </form>
    </section>
  );
}
